import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import ActionCenter from './components/ActionCenter'
import AuthCallback from './auth/AuthCallback'
import HealthCards from './components/HealthCards'
import Timeline from './components/Timeline'
import WorkloadView from './components/WorkloadView'
import { useAuth } from './auth/AuthContext'

type Tab = 'timeline' | 'health' | 'workload' | 'actions'

function MainApp() {
  const [activeTab, setActiveTab] = useState<Tab>('health')
  const { user, logout, isAuthenticated, idToken } = useAuth()

  // If we landed here without auth (real auth mode), kick off login.
  // The bypass-mode AuthContext returns isAuthenticated=true so this only
  // fires when VITE_AUTH_ENABLED=true and there's no live token.
  const { login } = useAuth()
  useEffect(() => {
    if (!isAuthenticated) void login(window.location.pathname)
  }, [isAuthenticated, login])

  if (!isAuthenticated) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Redirecting to sign-in…</div>
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'health', label: 'Health' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'workload', label: 'Workload' },
    { key: 'actions', label: 'Actions' },
  ]

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
      <header style={{ marginBottom: '2rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem' }}>ProjectBuddy Dashboard</h1>
          <p style={{ margin: '0.5rem 0 0', color: '#6b7280' }}>AI-powered project management for professional services</p>
        </div>
        {idToken && (
          <div style={{ fontSize: '0.875rem', color: '#374151', textAlign: 'right' }}>
            <div>{user?.email ?? user?.username ?? 'Signed in'}</div>
            <button
              onClick={logout}
              style={{ marginTop: '0.25rem', padding: '0.25rem 0.75rem', borderRadius: 4, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: '0.75rem' }}
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <nav style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              background: activeTab === t.key ? '#2563eb' : '#f3f4f6',
              color: activeTab === t.key ? 'white' : '#374151',
              fontWeight: activeTab === t.key ? '600' : '400',
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main>
        {activeTab === 'timeline' && <Timeline />}
        {activeTab === 'health' && <HealthCards />}
        {activeTab === 'workload' && <WorkloadView />}
        {activeTab === 'actions' && <ActionCenter />}
      </main>
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<MainApp />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
